"use client";

import React, { useState, useEffect, useRef } from "react";
import { FileText, Percent, ShieldCheck, Globe, ArrowRight, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.1,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const tabs = [{
  id: "fta",
  label: "FTA Overview",
  icon: "FileText"
}, {
  id: "tariffs",
  label: "Tariffs & Duties",
  icon: "Percent"
}, {
  id: "standards",
  label: "Standards & Certs",
  icon: "ShieldCheck"
}, {
  id: "digital",
  label: "Digital Trade",
  icon: "Globe"
}];
const ftaData = [{
  id: "fta-1",
  requirement: "Current Agreement Status",
  eu: "Active Negotiations (Relaunched 2022)",
  india: "Active Negotiations (Relaunched 2022)"
}, {
  id: "fta-2",
  requirement: "Primary Objectives",
  eu: "Eliminate 94%+ of tariffs, sustainability",
  india: "Market access for services, technology transfer"
}, {
  id: "fta-3",
  requirement: "Key Export Sectors",
  eu: "Machinery, Automotive, Chemicals",
  india: "IT Services, Textiles, Pharmaceuticals"
}, {
  id: "fta-4",
  requirement: "Investment Protection",
  eu: "Seeking separate Investment Protection Agreement",
  india: "Revising Bilateral Investment Treaties (BITs)"
}];
const tariffsData = [{
  id: "tar-1",
  requirement: "Average MFN Applied Tariff",
  eu: "~5.1% (Generally low across most sectors)",
  india: "~15.0% (Relatively higher, varies by sector)"
}, {
  id: "tar-2",
  requirement: "Automotive Imports",
  eu: "10% standard duty on passenger cars",
  india: "Up to 100% depending on vehicle value/type"
}, {
  id: "tar-3",
  requirement: "Machinery & Equipment",
  eu: "0% - 3% for most industrial machinery",
  india: "7.5% - 15% basic customs duty"
}, {
  id: "tar-4",
  requirement: "Customs Valuation Method",
  eu: "Transaction value (CIF basis)",
  india: "Transaction value (CIF basis)"
}];
const standardsData = [{
  id: "std-1",
  requirement: "Primary Regulatory Body",
  eu: "CEN, CENELEC, ETSI",
  india: "Bureau of Indian Standards (BIS)"
}, {
  id: "std-2",
  requirement: "Mandatory Certification",
  eu: "CE Marking for applicable product groups",
  india: "ISI Mark / Compulsory Registration Scheme (CRS)"
}, {
  id: "std-3",
  requirement: "Chemical Regulations",
  eu: "REACH (Registration, Evaluation, Authorisation)",
  india: "India REACH (Drafting phase, currently rules apply)"
}, {
  id: "std-4",
  requirement: "Food & Agriculture",
  eu: "EFSA (Strict MRLs, sanitary requirements)",
  india: "FSSAI (Specific labeling and ingredient norms)"
}];
const digitalData = [{
  id: "dig-1",
  requirement: "Data Privacy Framework",
  eu: "GDPR (General Data Protection Regulation)",
  india: "DPDP Act 2023 (Digital Personal Data Protection)"
}, {
  id: "dig-2",
  requirement: "Cross-Border Data Flows",
  eu: "Allowed to countries with 'adequacy' decisions",
  india: "Generally allowed, except to restricted nations"
}, {
  id: "dig-3",
  requirement: "E-commerce FDI Rules",
  eu: "Generally open, subject to national security screening",
  india: "100% in marketplace model; restricted in inventory model"
}, {
  id: "dig-4",
  requirement: "Digital Services Tax",
  eu: "Varies by member state (e.g., France 3%)",
  india: "2% Equalisation Levy on non-resident e-commerce"
}];
const renderIcon = (iconName: string, className?: string) => {
  switch (iconName) {
    case "FileText":
      return <FileText className={className} />;
    case "Percent":
      return <Percent className={className} />;
    case "ShieldCheck":
      return <ShieldCheck className={className} />;
    case "Globe":
      return <Globe className={className} />;
    default:
      return <Info className={className} />;
  }
};
export default function RegulatoryNavigator() {
  const [activeTab, setActiveTab] = useState("fta");
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.05
  });
  const getActiveData = () => {
    switch (activeTab) {
      case "fta":
        return ftaData;
      case "tariffs":
        return tariffsData;
      case "standards":
        return standardsData;
      case "digital":
        return digitalData;
      default:
        return ftaData;
    }
  };
  const activeData = getActiveData();
  return <section id="regulatory-navigator" className="bg-background text-foreground py-16 md:py-24 overflow-hidden" ref={ref}>
      <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        {}
        <div className="max-w-3xl mb-12">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4 text-primary">
            Regulatory Navigator
          </h2>
          <p className="text-lg text-muted-foreground">
            Navigate the complexities of cross-border trade between the European Union and India. 
            Compare key import/export requirements, tariffs, and standards to streamline your market entry.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {}
          <div className="lg:col-span-3 lg:sticky lg:top-24">
            {}
            <ScrollArea className="w-full whitespace-nowrap pb-4 lg:hidden">
              <div className="flex w-max space-x-2 px-1">
                {tabs.map((tab, i) => <div key={tab.id} data-index={i}>
                    <Button variant={activeTab === tab.id ? "default" : "outline"} onClick={() => setActiveTab(tab.id)} className={`justify-start ${activeTab === tab.id ? "bg-primary text-primary-foreground shadow-md" : "bg-card text-card-foreground hover:bg-accent"}`}>
                      {renderIcon(tab.icon, "mr-2 size-4")}
                      {tab.label}
                    </Button>
                  </div>)}
              </div>
              <ScrollBar orientation="horizontal" />
            </ScrollArea>

            {}
            <div className="hidden lg:block bg-card border border-border rounded-xl shadow-sm overflow-hidden">
              <div className="p-4 bg-muted/30 border-b border-border">
                <h3 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">
                  Trade Topics
                </h3>
              </div>
              <ScrollArea className="h-[300px] w-full">
                <div className="p-2 space-y-1">
                  {tabs.map((tab, i) => <div key={tab.id} data-index={i}>
                      <Button variant="ghost" onClick={() => setActiveTab(tab.id)} className={`w-full justify-start text-left font-medium transition-all ${activeTab === tab.id ? "bg-primary/10 text-primary hover:bg-primary/15" : "text-muted-foreground hover:text-foreground hover:bg-accent"}`}>
                        {renderIcon(tab.icon, `mr-3 size-5 ${activeTab === tab.id ? "text-primary" : "text-muted-foreground"}`)}
                        {tab.label}
                        {activeTab === tab.id && <ArrowRight className="ml-auto size-4 opacity-50" />}
                      </Button>
                    </div>)}
                </div>
              </ScrollArea>
            </div>
            
            {}
            <div className="hidden lg:flex mt-6 bg-secondary/10 border border-secondary/20 rounded-xl p-5 flex-col gap-3">
              <div className="flex items-center gap-2 text-secondary-foreground font-semibold">
                <Info className="size-5 text-secondary" />
                Need Expert Guidance?
              </div>
              <p className="text-sm text-muted-foreground">
                Our regulatory affairs specialists can help you navigate specific compliance requirements for your industry.
              </p>
              <Button variant="secondary" size="sm" className="w-full mt-2">
                Contact Compliance Team
              </Button>
            </div>
          </div>

          {}
          <div className="lg:col-span-9">
            <div className="bg-card border border-border rounded-xl shadow-sm overflow-hidden">
              <div className="p-6 md:p-8 border-b border-border bg-muted/10">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-primary/10 rounded-lg text-primary">
                    {renderIcon(tabs.find(t => t.id === activeTab)?.icon || "Info", "size-6")}
                  </div>
                  <h3 className="text-2xl font-bold text-foreground">
                    {tabs.find(t => t.id === activeTab)?.label}
                  </h3>
                </div>
                <p className="text-muted-foreground">
                  Comparative analysis of requirements between the European Union and India.
                </p>
              </div>

              <div className="p-0">
                <Table>
                  <TableHeader className="bg-muted/50">
                    <TableRow>
                      <TableHead className="w-[30%] font-semibold text-foreground py-4 pl-6">
                        Requirement / Area
                      </TableHead>
                      <TableHead className="w-[35%] font-semibold text-foreground py-4">
                        European Union
                      </TableHead>
                      <TableHead className="w-[35%] font-semibold text-foreground py-4 pr-6">
                        India
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {activeData.map((row, i) => <TableRow key={row.id} data-index={i} className="group">
                        <TableCell className="font-medium text-foreground py-4 pl-6 align-top">
                          {row.requirement}
                        </TableCell>
                        <TableCell className="text-muted-foreground py-4 align-top group-hover:text-foreground transition-colors">
                          {row.eu}
                        </TableCell>
                        <TableCell className="text-muted-foreground py-4 pr-6 align-top group-hover:text-foreground transition-colors">
                          {row.india}
                        </TableCell>
                      </TableRow>)}
                  </TableBody>
                </Table>
              </div>
              
              <div className="p-4 bg-muted/30 border-t border-border text-xs text-muted-foreground text-center">
                Data is for informational purposes only. Regulations are subject to change. Consult with our experts for the latest updates.
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}